/**
 * Google Places Autocomplete support for the single-string site address (S3.1, story 5599).
 *
 * The key is read from the environment, never hardcoded, and is a browser key by nature — the Maps
 * JS API loads it client-side, so the protection is an HTTP-referrer restriction on the key itself
 * in the Google Cloud console, not secrecy.
 *
 * Everything here degrades quietly: no key, a blocked script, a failed request or an empty result
 * all leave the caller with a plain text field. Free text is always allowed and a lookup never
 * blocks a save.
 */

const PLACES_SCRIPT_ID = "google-places-sdk";

export const googleMapsApiKey = (): string =>
  (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();

export const isPlacesConfigured = (): boolean => Boolean(googleMapsApiKey());

/** A details lookup that never calls back must not hang the form (bug 5695). */
const PLACE_DETAILS_TIMEOUT_MS = 8000;

/** Suggestions start at two characters (S3.1). */
export const MIN_ADDRESS_LOOKUP_LENGTH = 2;

export const shouldQueryPlaces = (term: string | null | undefined): boolean =>
  `${term ?? ""}`.trim().length >= MIN_ADDRESS_LOOKUP_LENGTH;

/** The two TDDA countries, as Google's `country` component spells them (short_name). */
const COUNTRY_NZ = "NZ";
const COUNTRY_AU = "AU";

/**
 * The country bias sent with a lookup. A client's own country wins; with none set the search covers
 * both TDDA countries rather than the whole world.
 */
export const placesCountryBias = (countryCode?: string | null): string[] => {
  const code = `${countryCode ?? ""}`.trim().toUpperCase();
  if (code === COUNTRY_NZ) return ["nz"];
  if (code === COUNTRY_AU) return ["au"];
  return ["nz", "au"];
};

let loader: Promise<boolean> | null = null;

/**
 * Loads the Places library once per page and resolves false — rather than throwing — when it cannot
 * be used, so a caller can simply fall back to a plain input.
 */
export const loadGooglePlaces = (): Promise<boolean> => {
  if (loader) return loader;

  loader = new Promise<boolean>((resolve) => {
    if (!isPlacesConfigured()) return resolve(false);

    const existing = (window as any).google?.maps?.places;
    if (existing) return resolve(true);

    const alreadyInjected = document.getElementById(PLACES_SCRIPT_ID);
    if (alreadyInjected) {
      alreadyInjected.addEventListener("load", () => resolve(true));
      alreadyInjected.addEventListener("error", () => resolve(false));
      return;
    }

    const script = document.createElement("script");
    script.id = PLACES_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      googleMapsApiKey(),
    )}&libraries=places`;
    script.onload = () => resolve(Boolean((window as any).google?.maps?.places));
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  return loader;
};

export type PlacePrediction = {
  placeId: string;
  /** The formatted address written into the single address string when a suggestion is picked. */
  description: string;
};

/**
 * A picked place, resolved far enough to place it geographically.
 *
 * The autocomplete prediction carries only a place id and a label, which is all the address field
 * itself needs. The site's TDDA Region is settled by which mapped territory the address falls inside
 * (bug 5695), so a selection now also resolves the coordinates and the country.
 */
export type PlaceDetails = {
  placeId: string;
  /** Google's formatted address, which is what gets written into the field. */
  formattedAddress: string;
  latitude: number | null;
  longitude: number | null;
  /** Two-letter country code, uppercased: "NZ", "AU". */
  countryCode: string | null;
} & AddressParts;

/**
 * The structured parts of a picked address, in the shape Imperans' import takes them (story 6356).
 *
 * The CRM stores the single formatted string as the address (decision 13), but Imperans writes two
 * Address rows per site and cannot split that string reliably — so when a place was picked, its
 * components travel beside the string and Imperans need not guess (bug 6494). Every part is null
 * when Google did not supply it; a typed address has none, and then the formatted string is
 * authoritative on its own.
 */
export type AddressParts = {
  /** "12/34 Example Street": unit (when any), number and street. Null without a street. */
  line1: string | null;
  suburb: string | null;
  townCity: string | null;
  /** The short form: "QLD", "NSW"; a region name in New Zealand. */
  state: string | null;
  postCode: string | null;
};

export const EMPTY_ADDRESS_PARTS: AddressParts = {
  line1: null,
  suburb: null,
  townCity: null,
  state: null,
  postCode: null,
};

/** Google's address component types, shared by Place Details and the Geocoder. */
const COMPONENT_TYPE = {
  subpremise: "subpremise",
  streetNumber: "street_number",
  route: "route",
  sublocality: "sublocality",
  sublocalityLevel1: "sublocality_level_1",
  locality: "locality",
  adminAreaLevel1: "administrative_area_level_1",
  adminAreaLevel2: "administrative_area_level_2",
  postalCode: "postal_code",
  country: "country",
} as const;

/**
 * Google's administrative_area_level_2 for an Australian address is the local government area, named
 * as a body — "Brisbane City Council", "Noosa Shire" — where the CRM wants the town or city.
 */
const AU_COUNCIL_SUFFIX = /\s+(City Council|Regional Council|Shire Council|Council|Shire)$/i;

const componentOf = (
  place: any,
  type: string,
  form: "long_name" | "short_name" = "long_name",
): string | null => {
  const components: any[] = place?.address_components || [];
  const match = components.find((c) => (c?.types || []).includes(type));
  const value = `${match?.[form] ?? ""}`.trim();
  return value || null;
};

/** The country code out of Google's address components, which is where the short form lives. */
export const countryCodeOf = (place: any): string | null => {
  const code = `${componentOf(place, COMPONENT_TYPE.country, "short_name") ?? ""}`.toUpperCase();
  return code || null;
};

/**
 * The address parts out of a Place Details or Geocoder result (story 6356, bug 6494).
 *
 * Google files the same facts under different component types in the two countries. In New Zealand
 * the suburb is a sublocality and the city is the locality; in Australia the suburb IS the locality
 * and the city is only recoverable from the local government area. Rather than return whichever
 * component happens to exist and let Imperans receive "Brisbane City" as a town in one record and a
 * suburb in the next, the country decides the reading. A place with no components yields all null.
 */
export const addressPartsOf = (place: any): AddressParts => {
  const isAustralia = countryCodeOf(place) === COUNTRY_AU;

  const route = componentOf(place, COMPONENT_TYPE.route);
  const streetNumber = componentOf(place, COMPONENT_TYPE.streetNumber);
  const unit = componentOf(place, COMPONENT_TYPE.subpremise);
  const line1 = route
    ? `${unit ? `${unit}/` : ""}${streetNumber ? `${streetNumber} ` : ""}${route}`.trim()
    : null;

  const locality = componentOf(place, COMPONENT_TYPE.locality);
  const suburb = isAustralia
    ? locality
    : componentOf(place, COMPONENT_TYPE.sublocalityLevel1) ??
      componentOf(place, COMPONENT_TYPE.sublocality);

  const council = componentOf(place, COMPONENT_TYPE.adminAreaLevel2);
  const townCity = isAustralia
    ? (council ? council.replace(AU_COUNCIL_SUFFIX, "").trim() : null) || locality
    : locality;

  return {
    line1,
    suburb,
    townCity,
    state: componentOf(place, COMPONENT_TYPE.adminAreaLevel1, "short_name"),
    postCode: componentOf(place, COMPONENT_TYPE.postalCode),
  };
};

/**
 * Resolves a free-text address to coordinates and a country, the way a bulk-imported CSV row needs
 * to (bug 5826): unlike the manual form, an imported row was never picked from the autocomplete
 * dropdown, so there is no place id to look up — this geocodes the text itself instead. Same
 * degrade-quietly contract as the rest of this file: no key, a blocked script, an unmatched address
 * or a timeout all resolve null rather than reject, so an import never hangs or aborts on this.
 */
export const geocodeAddress = async (address: string | null | undefined): Promise<PlaceDetails | null> => {
  const query = `${address ?? ""}`.trim();
  if (!query) return null;
  if (!(await loadGooglePlaces())) return null;

  const maps = (window as any).google?.maps;
  if (!maps) return null;

  return new Promise<PlaceDetails | null>((resolve) => {
    let settled = false;
    const finish = (value: PlaceDetails | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const timeout = setTimeout(() => finish(null), PLACE_DETAILS_TIMEOUT_MS);

    try {
      new maps.Geocoder().geocode({ address: query }, (results: any[], status: any) => {
        clearTimeout(timeout);
        if (status !== maps.GeocoderStatus?.OK || !results?.length) return finish(null);

        const place = results[0];
        const location = place.geometry?.location;
        const lat = typeof location?.lat === "function" ? location.lat() : null;
        const lng = typeof location?.lng === "function" ? location.lng() : null;

        finish({
          placeId: place.place_id || "",
          formattedAddress: place.formatted_address || query,
          latitude: Number.isFinite(lat) ? lat : null,
          longitude: Number.isFinite(lng) ? lng : null,
          countryCode: countryCodeOf(place),
          ...addressPartsOf(place),
        });
      });
    } catch {
      clearTimeout(timeout);
      finish(null);
    }
  });
};

/**
 * One autocomplete session, so a burst of keystrokes and the selection that ends it are billed and
 * grouped as a single lookup. Call `renew()` after a selection to begin the next session.
 */
export class PlacesSession {
  private service: any = null;
  private token: any = null;

  async ready(): Promise<boolean> {
    const available = await loadGooglePlaces();
    if (!available) return false;
    const places = (window as any).google?.maps?.places;
    if (!places) return false;
    if (!this.service) this.service = new places.AutocompleteService();
    if (!this.token) this.token = new places.AutocompleteSessionToken();
    return true;
  }

  renew(): void {
    const places = (window as any).google?.maps?.places;
    this.token = places ? new places.AutocompleteSessionToken() : null;
  }

  async predict(term: string, countryCode?: string | null): Promise<PlacePrediction[]> {
    if (!shouldQueryPlaces(term)) return [];
    if (!(await this.ready())) return [];

    return new Promise<PlacePrediction[]>((resolve) => {
      this.service.getPlacePredictions(
        {
          input: term.trim(),
          sessionToken: this.token,
          componentRestrictions: { country: placesCountryBias(countryCode) },
        },
        (predictions: any[]) => {
          resolve(
            (predictions || []).map((prediction) => ({
              placeId: prediction.place_id,
              description: prediction.description,
            })),
          );
        },
      );
    });
  }

  /**
   * The coordinates and country behind a picked suggestion.
   *
   * Asks for three fields only. Place Details is billed by the field groups requested, and geometry
   * plus address components is the cheapest tier that answers "where is this"; asking for everything
   * would cost more per selection for data nothing reads.
   *
   * Resolves null rather than rejecting when the lookup cannot run or comes back empty, because a
   * selection must still fill the address field even if the region cannot be worked out.
   */
  async details(placeId: string, fallbackAddress?: string): Promise<PlaceDetails | null> {
    if (!placeId) return null;
    if (!(await this.ready())) return null;

    const places = (window as any).google?.maps?.places;
    const maps = (window as any).google?.maps;
    if (!places || !maps) return null;

    // PlacesService needs a node to attach its attributions to; it is never rendered.
    const service = new places.PlacesService(document.createElement("div"));

    return new Promise<PlaceDetails | null>((resolve) => {
      let settled = false;
      const finish = (value: PlaceDetails | null) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      // Google's callback is not guaranteed to fire; without this a failed lookup would leave the
      // caller awaiting forever and the form's busy state stuck on.
      const timeout = setTimeout(() => finish(null), PLACE_DETAILS_TIMEOUT_MS);

      try {
        service.getDetails(
          {
            placeId,
            fields: ["place_id", "geometry.location", "address_components", "formatted_address"],
            sessionToken: this.token,
          },
          (place: any, status: any) => {
            clearTimeout(timeout);
            if (status !== maps.places.PlacesServiceStatus.OK || !place) return finish(null);

            const location = place.geometry?.location;
            const lat = typeof location?.lat === "function" ? location.lat() : null;
            const lng = typeof location?.lng === "function" ? location.lng() : null;

            finish({
              placeId: place.place_id || placeId,
              formattedAddress: place.formatted_address || fallbackAddress || "",
              latitude: Number.isFinite(lat) ? lat : null,
              longitude: Number.isFinite(lng) ? lng : null,
              countryCode: countryCodeOf(place),
              // The same request already pays for address_components (they carry the country), so
              // the structured parts cost nothing extra (bug 6494).
              ...addressPartsOf(place),
            });
          },
        );
      } catch {
        clearTimeout(timeout);
        finish(null);
      }
    });
  }
}
